import React from 'react';
import { View, ScrollView, TouchableOpacity, Pressable, ActivityIndicator, TextInput, Dimensions } from 'react-native';
import Text from 'app/components/Text';
import Tag from 'app/components/chapter/Tag';
import TarotCard from 'app/components/TarotCard';
import HoverableTooltip from 'app/components/HoverableTooltip';
import valueActions from 'app/utils/valueActions';
import chapterActions from 'app/utils/chapterActions';
import valueHelpers from 'app/utils/valueHelpers';
import { parseCard, getCardName, computeStats, ELEMENT_COLORS, ELEMENT_LABELS } from 'app/utils/tarotHelpers';
import { Ionicons } from '@expo/vector-icons';
import { StyleContext } from 'app/contexts/Style';
import store from 'app/redux/store';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default class CalendarView extends React.Component {
  static contextType = StyleContext;

  constructor(props) {
    super(props);
    const { navigation } = props;
    navigation.setOptions({
      headerRight: () => (
        <TouchableOpacity onPress={navigation.toggleDrawer} style={{ marginRight: 16, padding: 4 }}>
          <Ionicons name="menu" size={28} color={this.context?.colors?.darkForeground || '#fff'} />
        </TouchableOpacity>
      ),
    });

    const { values, chapters } = store.getState();
    const items = this.getItems(values);
    this.state = {
      currentMonth: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      dayMap: this.buildDayMap(items, chapters),
      viewMode: 'month',
      loading: false,
      tarotMode: false,
      searchMode: false,
      searchQuery: '',
      searchSuggestions: [],
      searchValue: null,
      searchDayMap: {},
      searchLoading: false,
    };
  }

  async componentDidMount() {
    this._unsubscribe = store.subscribe(() => {
      const { values, chapters } = store.getState();
      const items = this.getItems(values);
      this.setState({ dayMap: this.buildDayMap(items, chapters) });
    });
    const { currentMonth } = this.state;
    this.setState({ loading: true });
    // the index is slow on the old box and only supplements days without
    // chapters — load it in the background, don't block first paint
    valueActions.indexLite();
    await chapterActions.calendar(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1),
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0),
    );
    this.setState({ loading: false });
  }

  fetchChaptersForMonth = async (monthDate) => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    this.setState({ loading: true });
    await chapterActions.calendar(new Date(year, month, 1), new Date(year, month + 1, 0));
    this.setState({ loading: false });
  };

  fetchChaptersForYear = async (year) => {
    this.setState({ loading: true });
    await chapterActions.calendar(new Date(year, 0, 1), new Date(year, 11, 31));
    this.setState({ loading: false });
  };

  componentWillUnmount() {
    this._unsubscribe?.();
  }

  getItems = (values) => {
    const allValues = valueHelpers.filterToCurrentRevisions(values);
    const items = [];
    allValues.forEach(v => {
      const completedAt = valueHelpers.getCompletedAt(v);
      (v.progress_at || []).forEach((ts, i) => {
        items.push({ uid: `${v.id}_progress_${i}`, timestamp: ts, value: v });
      });
      if (completedAt) {
        const lastProgress = v.progress_at?.[v.progress_at.length - 1];
        if (!lastProgress || new Date(lastProgress).toDateString() !== new Date(completedAt).toDateString()) {
          items.push({ uid: `${v.id}_completed`, timestamp: completedAt, value: v });
        }
      }
      if (!completedAt && !v.progress_at?.length) {
        items.push({ uid: `${v.id}_created`, timestamp: v.created_at, value: v });
      }
    });
    return items;
  };

  buildDayMap = (items, chapters = null) => {
    const storeChapters = chapters || store.getState().chapters || [];
    const map = {};

    storeChapters.forEach(chapter => {
      if (!chapter.start_at || chapter.range !== 1) return;
      const day = new Date(chapter.start_at + 'T12:00:00').toDateString();
      const seen = new Set();
      map[day] = (chapter.moments || [])
        .filter(m => m.value)
        .filter(m => {
          const vid = m.value_id ?? m.value?.id;
          if (seen.has(vid)) return false;
          seen.add(vid);
          return true;
        })
        .map(m => ({
          uid: `${m.value_id}_chapter_${chapter.id}`,
          timestamp: chapter.start_at,
          value: m.value,
          value_id: m.value_id ?? m.value.id,
          momentText: m.text || null,
        }));
    });

    items.forEach(item => {
      const day = new Date(item.timestamp).toDateString();
      if (map[day]) return;
      map[day] = [];
      if (map[day].find(m => m.value_id === item.value.id)) return;
      map[day].push({ uid: item.uid, timestamp: item.timestamp, value: item.value, value_id: item.value.id });
    });

    return map;
  };

  getMonthDays = () => {
    const { currentMonth } = this.state;
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    for (let i = 0; i < firstDay.getDay(); i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(year, month, d));
    while (days.length % 7 !== 0) days.push(null);
    return days;
  };

  getTarotChildIds = () => {
    const { values } = store.getState();
    const allValues = Array.isArray(values) ? values : Object.values(values || {});
    const dailyPulls = allValues.find(v =>
      (v.text || v.title || '').toLowerCase().trim() === 'daily pulls'
    );
    return dailyPulls?.child_ids || [];
  };

  filterToTarot = (moments) => {
    const ids = this.getTarotChildIds();
    return ids.length > 0 ? moments.filter(m => ids.includes(m.value_id)) : moments;
  };

  computeTarotStats = () => {
    const { dayMap, viewMode, currentMonth } = this.state;
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const allMoments = [];

    Object.entries(dayMap).forEach(([dateStr, moments]) => {
      const d = new Date(dateStr);
      if (viewMode === 'year' && d.getFullYear() !== year) return;
      if (viewMode === 'month' && (d.getFullYear() !== year || d.getMonth() !== month)) return;
      allMoments.push(...moments);
    });

    const filtered = this.filterToTarot(allMoments);
    const cards = filtered.map(m => parseCard(getCardName(m))).filter(Boolean);
    return computeStats(cards);
  };

  prevPeriod = () => {
    const { viewMode, currentMonth, searchMode, searchValue } = this.state;
    if (viewMode === 'year') {
      const y = currentMonth.getFullYear() - 1;
      this.fetchChaptersForYear(y);
      this.setState({ currentMonth: new Date(y, currentMonth.getMonth(), 1) }, () => {
        if (searchMode && searchValue) this.fetchSearchMoments();
      });
    } else {
      const next = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
      this.fetchChaptersForMonth(next);
      this.setState({ currentMonth: next }, () => {
        if (searchMode && searchValue) this.fetchSearchMoments();
      });
    }
  };

  nextPeriod = () => {
    const { viewMode, currentMonth, searchMode, searchValue } = this.state;
    if (viewMode === 'year') {
      const y = currentMonth.getFullYear() + 1;
      this.fetchChaptersForYear(y);
      this.setState({ currentMonth: new Date(y, currentMonth.getMonth(), 1) }, () => {
        if (searchMode && searchValue) this.fetchSearchMoments();
      });
    } else {
      const next = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
      this.fetchChaptersForMonth(next);
      this.setState({ currentMonth: next }, () => {
        if (searchMode && searchValue) this.fetchSearchMoments();
      });
    }
  };

  goToday = () => {
    this.setState({ currentMonth: new Date(new Date().getFullYear(), new Date().getMonth(), 1) });
  };

  toggleViewMode = () => {
    this.setState(({ viewMode, currentMonth }) => {
      const next = viewMode === 'month' ? 'year' : 'month';
      if (next === 'year') this.fetchChaptersForYear(currentMonth.getFullYear());
      return { viewMode: next };
    }, () => {
      const { searchMode, searchValue } = this.state;
      if (searchMode && searchValue) this.fetchSearchMoments();
    });
  };

  toggleTarotMode = () => {
    this.setState(s => ({ tarotMode: !s.tarotMode }));
  };

  toggleSearchMode = () => {
    this.setState(s => ({
      searchMode: !s.searchMode,
      searchQuery: '',
      searchSuggestions: [],
      searchValue: null,
      searchDayMap: {},
    }));
  };

  getSearchDateRange = () => {
    const { viewMode, currentMonth } = this.state;
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    if (viewMode === 'year') {
      return { startDate: new Date(year, 0, 1), endDate: new Date(year, 11, 31) };
    }
    return { startDate: new Date(year, month, 1), endDate: new Date(year, month + 1, 0) };
  };

  fetchSearchMoments = async (value) => {
    const val = value || this.state.searchValue;
    if (!val) return;
    this.setState({ searchLoading: true });
    const { startDate, endDate } = this.getSearchDateRange();
    const res = await valueActions.calendarMoments(val.id, startDate, endDate);
    if (res?.days) {
      this.setState({ searchDayMap: this.buildSearchDayMap(res.days) });
    }
    this.setState({ searchLoading: false });
  };

  buildSearchDayMap = (days) => {
    const map = {};
    (days || []).forEach(day => {
      const dateStr = new Date(day.start_at + 'T12:00:00').toDateString();
      const seen = new Set();
      map[dateStr] = (day.moments || [])
        .filter(m => m.value)
        .filter(m => {
          const vid = m.value_id ?? m.value?.id;
          if (seen.has(vid)) return false;
          seen.add(vid);
          return true;
        })
        .map(m => ({
          uid: `search_${m.id}`,
          timestamp: day.start_at,
          value: m.value,
          value_id: m.value_id ?? m.value?.id,
          momentText: m.text || null,
        }));
    });
    return map;
  };

  handleSearchQueryChange = (text) => {
    this.setState({ searchQuery: text, searchValue: text ? this.state.searchValue : null });
    if (this._searchTimeout) clearTimeout(this._searchTimeout);
    if (!text.length) {
      this.setState({ searchSuggestions: [], searchDayMap: {} });
      return;
    }
    this._searchTimeout = setTimeout(async () => {
      const results = await valueActions.autocompleteValues(text, 0);
      this.setState({ searchSuggestions: Array.isArray(results) ? results.slice(0, 8) : [] });
    }, 300);
  };

  handleSearchSelect = async (value) => {
    this.setState({ searchValue: value, searchQuery: value.title || value.text || '', searchSuggestions: [] });
    await this.fetchSearchMoments(value);
  };

  getActiveDayMap = () => {
    const { searchMode, searchValue, searchDayMap, dayMap } = this.state;
    return (searchMode && searchValue) ? searchDayMap : dayMap;
  };

  renderSearchPanel = () => {
    const { colors, sizes } = this.context;
    const { searchQuery, searchSuggestions, searchValue, searchLoading } = this.state;
    return (
      <View style={{ marginBottom: 8 }}>
        <View style={{
          flexDirection: 'row', alignItems: 'center',
          backgroundColor: colors.fill, borderRadius: 8,
          paddingHorizontal: 10, paddingVertical: 6, gap: 8,
        }}>
          <TextInput
            value={searchQuery}
            onChangeText={this.handleSearchQueryChange}
            placeholder="Search values…"
            placeholderTextColor={colors.trim}
            autoFocus
            style={{ flex: 1, fontSize: sizes.default, color: colors.foreground }}
          />
          {searchLoading && <ActivityIndicator size="small" color={colors.trim} />}
          {searchValue && (
            <TouchableOpacity onPress={() => this.setState({ searchValue: null, searchDayMap: {}, searchQuery: '' })}>
              <Ionicons name="close-circle" size={18} color={colors.trim} />
            </TouchableOpacity>
          )}
        </View>

        {searchSuggestions.length > 0 && (
          <View style={{
            backgroundColor: colors.fill, borderRadius: 8,
            marginTop: 4, maxHeight: 200, overflow: 'hidden',
          }}>
            <ScrollView nestedScrollEnabled keyboardShouldPersistTaps="always">
              {searchSuggestions.map(v => (
                <TouchableOpacity
                  key={v.id}
                  onPress={() => this.handleSearchSelect(v)}
                  style={{ paddingHorizontal: 10, paddingVertical: 8, borderBottomWidth: 1, borderColor: colors.trim + '44' }}
                >
                  <Tag value={v} small icon />
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        )}

        {searchValue && (
          <View style={{
            flexDirection: 'row', alignItems: 'center',
            backgroundColor: colors.fill, borderRadius: 8,
            padding: 8, marginTop: 4, gap: 8,
          }}>
            <Tag value={searchValue} icon />
            <Text style={{ fontSize: sizes.small, color: colors.trim }}>history</Text>
          </View>
        )}
      </View>
    );
  };

  handleDayPress = (date) => {
    this.props.navigation.navigate('Chapters', { date: date.toString(), range: 'day' });
  };

  handleValuePress = (m) => {
    const id = m.value_id ?? m.value?.id;
    if (id) this.props.navigation.navigate('Value', { id });
  };

  renderTarotStats = () => {
    const { colors, sizes } = this.context;
    const rankSymbol = (rank) => {
      if (rank === 'ace') return 'A';
      if (rank === 'page' || rank === 'princess') return '🜃';
      if (rank === 'knight') return '🜂';
      if (rank === 'prince' || rank === 'king') return '🜁';
      if (rank === 'queen') return '🜄';
      if (!isNaN(parseInt(rank))) return String(parseInt(rank));
      return rank;
    };
    const stats = this.computeTarotStats();
    if (!stats || !stats.total) return null;

    const sm = sizes.smallFont;
    const divider = <View style={{ height: 1, backgroundColor: colors.trim + '44', marginVertical: 6 }} />;

    // Suit color lookup by suit name
    const SUIT_COLOR_MAP = {
      wands: '#E07820', fire: '#E07820', bows: '#E07820',
      cups: '#4A8FD4', water: '#4A8FD4', vessels: '#4A8FD4',
      swords: '#C4A800', air: '#C4A800', arrows: '#C4A800',
      pentacles: '#8B5E1A', earth: '#8B5E1A', stones: '#8B5E1A', coins: '#8B5E1A', disks: '#8B5E1A', discs: '#8B5E1A',
    };

    return (
      <View style={{ padding: 10, backgroundColor: colors.fill, borderRadius: 8, marginBottom: 8 }}>

        {/* Header */}
        <Text style={{ fontSize: sm, fontWeight: '700', color: colors.foreground, marginBottom: 6 }}>
          {stats.total} pulls{stats.reversedCount > 0 ? `  ·  ↶ ${Math.round((stats.reversedCount / stats.total) * 100)}% reversed` : ''}
        </Text>

        {/* Suits by frequency (including majors) */}
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          {[...stats.suitsByFrequency, ...(stats.elements.major > 0 ? [{ suit: 'major', count: stats.elements.major }] : [])]
            .sort((a, b) => b.count - a.count)
            .map(({ suit, count }) => {
              const color = suit === 'major' ? '#9B59B6' : (SUIT_COLOR_MAP[suit] || colors.trim);
              const pct = Math.round((count / stats.total) * 100);
              return (
                <View key={suit} style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                  <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: color }} />
                  <Text style={{ fontSize: sm, color: colors.foreground, fontWeight: '600' }}>
                    {suit} <Text style={{ fontWeight: '400', color: colors.foreground }}>{pct}%</Text>
                  </Text>
                </View>
              );
            })}
        </View>

        {/* Repeated cards */}
        {stats.repeatedCards.length > 0 && (
          <>
            {divider}
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              {stats.repeatedCards.map(({ name, count }) => {
                const card = parseCard(name);
                return (
                  <View key={name} style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                    <TarotCard card={card} size="small" />
                    <Text style={{ fontSize: sm, color: colors.foreground }}>×{count}</Text>
                  </View>
                );
              })}
            </View>
          </>
        )}

        {/* Repeated ranks */}
        {stats.repeatedRanks.length > 0 && (
          <>
            {divider}
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              {stats.repeatedRanks.map(({ rank, count }) => {
                const symbol = rankSymbol(rank);
                return (
                  <View key={rank} style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                    <View style={{
                      width: 20, height: 20, borderRadius: 10,
                      backgroundColor: colors.fill,
                      alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Text style={{ fontSize: 11, color: colors.foreground, fontWeight: '700', lineHeight: 20, textAlign: 'center' }}>
                        {symbol}
                      </Text>
                    </View>
                    <Text style={{ fontSize: sm, color: colors.foreground }}>×{count}</Text>
                  </View>
                );
              })}
            </View>
          </>
        )}

      </View>
    );
  };

  renderMiniMonth = (year, monthIndex) => {
    const { colors, sizes } = this.context;
    const { tarotMode } = this.state;
    const dayMap = this.getActiveDayMap();
    const today = new Date().toDateString();
    const firstDay = new Date(year, monthIndex, 1);
    const lastDate = new Date(year, monthIndex + 1, 0).getDate();
    const monthName = firstDay.toLocaleString('default', { month: 'short' });
    const days = [];
    for (let i = 0; i < firstDay.getDay(); i++) days.push(null);
    for (let d = 1; d <= lastDate; d++) days.push(new Date(year, monthIndex, d));
    while (days.length % 7 !== 0) days.push(null);

    return (
      <View key={monthIndex} style={{ flex: 1, padding: 6 }}>
        <Text style={{ textAlign: 'center', fontSize: sizes.small, fontWeight: '700', color: colors.foreground, marginBottom: 4 }}>
          {monthName}
        </Text>
        <View style={{ flexDirection: 'row', marginBottom: 2 }}>
          {['S','M','T','W','T','F','S'].map((d, i) => (
            <View key={i} style={{ flex: 1, alignItems: 'center' }}>
              <Text style={{ fontSize: 8, color: colors.trim }}>{d}</Text>
            </View>
          ))}
        </View>
        {chunk(days, 7).map((week, wi) => (
          <View key={wi} style={{ flexDirection: 'row' }}>
            {week.map((day, di) => {
              if (!day) return <View key={di} style={{ flex: 1, height: 22 }} />;
              const dateStr = day.toDateString();
              const allMoments = dayMap[dateStr] || [];
              const moments = tarotMode ? this.filterToTarot(allMoments) : allMoments;
              const isToday = dateStr === today;

              const dotColors = !tarotMode
                ? moments.map(m => valueHelpers.colorAsHex(m.value?.color)).filter(Boolean)
                : [];

              const tarotCards = tarotMode
                ? moments.map(m => parseCard(getCardName(m))).filter(Boolean)
                : [];

              const tooltipText = moments.length > 0
                ? moments.map(m => m.value?.title || m.value?.text || '').filter(Boolean).join('\n')
                : null;

              const cell = (
                <Pressable
                  hitSlop={{ top: 3, bottom: 3, left: 3, right: 3 }}
                  style={({ hovered }) => ({
                    flex: 1,
                    height: 22,
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    borderRadius: 3,
                    backgroundColor: hovered ? colors.trim + '55' : 'transparent',
                    cursor: 'pointer',
                  })}
                  onPress={() => this.handleDayPress(day)}
                >
                  {dotColors.length > 0 && (
                    <View style={{
                      position: 'absolute', top: 1, left: 1, right: 1, bottom: 1,
                      flexDirection: 'row', flexWrap: 'wrap',
                      alignContent: 'center', justifyContent: 'center',
                      overflow: 'hidden',
                    }}>
                      {dotColors.map((color, ci) => (
                        <View key={ci} style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: color, margin: 1, opacity: 0.85 }} />
                      ))}
                    </View>
                  )}
                  {tarotCards.length > 0 && (
                    <View style={{
                      position: 'absolute', top: 1, left: 1, right: 1, bottom: 1,
                      flexDirection: 'row', flexWrap: 'wrap',
                      alignContent: 'center', justifyContent: 'center',
                      overflow: 'hidden',
                    }}>
                      {tarotCards.map((card, ci) => (
                        <TarotCard key={ci} card={card} size="tiny" />
                      ))}
                    </View>
                  )}
                  <Text style={{
                    fontSize: 9,
                    color: isToday ? colors.active : colors.foreground,
                    fontWeight: isToday ? '700' : '400',
                    opacity: isToday ? 1 : 0.35,
                    zIndex: 1,
                  }}>
                    {day.getDate()}
                  </Text>
                </Pressable>
              );

              return tooltipText ? (
                <HoverableTooltip key={di} text={tooltipText} style={{ flex: 1, height: 22 }}>
                  {cell}
                </HoverableTooltip>
              ) : (
                <View key={di} style={{ flex: 1, height: 22 }}>
                  {cell}
                </View>
              );
            })}
          </View>
        ))}
      </View>
    );
  };

  render() {
    const { colors, sizes } = this.context;
    const { currentMonth, viewMode, loading, tarotMode, searchMode, searchLoading } = this.state;
    const dayMap = this.getActiveDayMap();
    const days = this.getMonthDays();
    const today = new Date().toDateString();
    const year = currentMonth.getFullYear();
    const label = viewMode === 'year'
      ? `${year}`
      : currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' });

    return (
      <View style={{ flex: 1, padding: 12, backgroundColor: colors.background }}>

        {/* Navigation header: period row, then mode controls — the old
            single row overflowed phone widths */}
        <View style={{ marginBottom: 8 }}>
          <View style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingVertical: 4,
            paddingHorizontal: 4,
          }}>
            <TouchableOpacity onPress={this.prevPeriod} style={{ padding: 8 }}>
              <Ionicons name="chevron-back" size={22} color={colors.foreground} />
            </TouchableOpacity>
            <TouchableOpacity onPress={this.goToday}>
              <Text style={{ fontSize: sizes.large, fontWeight: '600', color: colors.foreground }}>
                {label}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={this.nextPeriod} style={{ padding: 8 }}>
              <Ionicons name="chevron-forward" size={22} color={colors.foreground} />
            </TouchableOpacity>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
            <TouchableOpacity
              onPress={this.toggleViewMode}
              style={{
                paddingHorizontal: 12, paddingVertical: 6,
                borderRadius: 12,
                backgroundColor: colors.trim + '44',
              }}
            >
              <Text style={{ fontSize: sizes.default, color: colors.foreground, fontWeight: '600' }}>
                {viewMode === 'month' ? 'Year' : 'Month'}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={this.toggleTarotMode}
              style={{
                paddingHorizontal: 12, paddingVertical: 6,
                borderRadius: 12,
                backgroundColor: tarotMode ? '#9B59B6' : colors.trim + '44',
              }}
            >
              <Text style={{ fontSize: sizes.default, color: tarotMode ? '#fff' : colors.foreground, fontWeight: '600' }}>
                Tarot
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={this.toggleSearchMode}
              style={{
                paddingHorizontal: 12, paddingVertical: 6,
                borderRadius: 12,
                backgroundColor: searchMode ? colors.active : colors.trim + '44',
              }}
            >
              <Text style={{ fontSize: sizes.default, color: searchMode ? colors.background : colors.foreground, fontWeight: '600' }}>
                Search
              </Text>
            </TouchableOpacity>
            {(loading || searchLoading) && <ActivityIndicator size="small" color={colors.trim} style={{ marginLeft: 4 }} />}
          </View>
        </View>

        {tarotMode && this.renderTarotStats()}
        {searchMode && this.renderSearchPanel()}

        {viewMode === 'year' ? (
          /* Year view: 4 mini-months per row — 2 on phone widths */
          <ScrollView style={{ flex: 1 }}>
            {chunk([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11], Dimensions.get('window').width < 560 ? 2 : 4).map((row, ri) => (
              <View key={ri} style={{ flexDirection: 'row', marginBottom: 16 }}>
                {row.map(mi => this.renderMiniMonth(year, mi))}
              </View>
            ))}
          </ScrollView>
        ) : (
          /* Month view */
          <View style={{ flex: 1 }}>
            <View style={{ flexDirection: 'row', marginBottom: 4 }}>
              {DAY_NAMES.map(name => (
                <View key={name} style={{ width: '14.285714%', alignItems: 'center', paddingVertical: 6 }}>
                  <Text style={{ fontSize: sizes.default, color: colors.trim, fontWeight: '600' }}>{name}</Text>
                </View>
              ))}
            </View>
            <ScrollView style={{ flex: 1 }}>
              <View style={{ borderLeftWidth: 1, borderBottomWidth: 1, borderColor: colors.trim }}>
                {chunk(days, 7).map((week, wi) => (
                  <View key={wi} style={{ flexDirection: 'row', borderTopWidth: 1, borderColor: colors.trim }}>
                    {week.map((day, di) => {
                      const isNull = !day;
                      const dateStr = day?.toDateString();
                      const allMoments = !isNull ? (dayMap[dateStr] || []) : [];
                      const moments = tarotMode ? this.filterToTarot(allMoments) : allMoments;
                      const isToday = dateStr === today;
                      const cellStyle = { width: '14.285714%', minHeight: 100, borderRightWidth: 1, borderColor: colors.trim };

                      if (isNull) return <View key={di} style={{ ...cellStyle, opacity: 0.4 }} />;

                      return (
                        // the WHOLE cell opens the day — the 26px date circle
                        // alone was an impossible target on mobile
                        <Pressable
                          key={di}
                          onPress={() => this.handleDayPress(day)}
                          style={({ hovered }) => ({
                            ...cellStyle,
                            maxHeight: 180,
                            padding: 4,
                            overflow: 'hidden',
                            backgroundColor: hovered ? colors.trim + '33' : 'transparent',
                            cursor: 'pointer',
                          })}
                        >
                          <View
                            style={{
                              width: 26, height: 26, borderRadius: 13,
                              backgroundColor: isToday ? colors.active : 'transparent',
                              alignItems: 'center', justifyContent: 'center',
                              marginBottom: 4,
                            }}
                          >
                            <Text style={{
                              fontSize: sizes.base,
                              color: isToday ? colors.background : colors.foreground,
                              fontWeight: isToday ? '700' : '400',
                            }}>
                              {day.getDate()}
                            </Text>
                          </View>
                          {moments.length > 0 && (
                            <ScrollView nestedScrollEnabled showsVerticalScrollIndicator={false}>
                              {moments.map(m => {
                                if (tarotMode) {
                                  const cardName = getCardName(m);
                                  const card = parseCard(cardName);
                                  const label = cardName || m.value?.text || m.value?.title;
                                  return (
                                    <TouchableOpacity
                                      key={m.uid}
                                      onPress={() => this.handleValuePress(m)}
                                      style={{ marginBottom: 3 }}
                                    >
                                      <HoverableTooltip text={label}>
                                        <TarotCard card={card} size="small" />
                                      </HoverableTooltip>
                                    </TouchableOpacity>
                                  );
                                }
                                return (
                                  <TouchableOpacity
                                    key={m.uid}
                                    onPress={() => this.handleValuePress(m)}
                                    style={{ marginBottom: 2, overflow: 'hidden' }}
                                  >
                                    <Tag
                                      value={m.value}
                                      small
                                      icon
                                      maxWidth={Dimensions.get('window').width < 560 ? 56 : 200}
                                    />
                                  </TouchableOpacity>
                                );
                              })}
                            </ScrollView>
                          )}
                        </Pressable>
                      );
                    })}
                  </View>
                ))}
              </View>
            </ScrollView>
          </View>
        )}
      </View>
    );
  }
}

function chunk(arr, size) {
  const result = [];
  for (let i = 0; i < arr.length; i += size) result.push(arr.slice(i, i + size));
  return result;
}
